import { useState, useCallback, useRef, useMemo, useEffect, type Dispatch, type SetStateAction } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import type { PersonSearchResult, Project } from '@/api/client';
import { useAuth } from '@/contexts/useAuth';
import { useToast } from '@/contexts/ToastContext';
import { format, isValid, parse } from 'date-fns';
import { Modal } from '@/components/ui/Modal/Modal';
import { ChoiceDialog } from '@/components/ui/ChoiceDialog/ChoiceDialog';
import { Input } from '@/components/ui/Input/Input';
import { StatusSelect } from '@/components/StatusSelect/StatusSelect';
import { SelectDropdown } from '@/components/ui/SelectDropdown/SelectDropdown';
import { Button } from '@/components/ui/Button/Button';
import { AddIcon } from '@/components/ui/Icons';
import type { CreatePersonCreatedPayload, CreatePersonRolePreset } from '@/components/CreatePersonModal/CreatePersonModal';
import { useCatalogManageOpener, useCanManageCatalog } from '@/contexts/CatalogManageOpenerContext';
import { PROJECT_TYPES, PROJECT_TYPE_DISPLAY, getProjectTypeColor, type ProjectTypeValue } from '@/utils/projectTypeColors';
import { ProjectTypeSymbol } from '@/utils/ProjectTypeSymbol';
import {
  ReaderPriceList,
  entriesToReaderInputs,
  isValidReaderTimeInput,
  readerCatalogToEntry,
  type ReaderEntryItem,
} from '@/components/ReaderPriceList/ReaderPriceList';
import { AuthorCombo, type AuthorValueItem } from '@/components/AuthorCombo/AuthorCombo';
import { ContractorServiceList, type ContractorEntryItem, entriesToInputs } from '@/components/ContractorServiceList/ContractorServiceList';
import { ProducerCombo, type ProducerValueItem } from '@/components/ProducerCombo/ProducerCombo';
import { TecheditorCombo, type TecheditorValueItem } from '@/components/TecheditorCombo/TecheditorCombo';
import { CorrectorCombo, type CorrectorValueItem } from '@/components/CorrectorCombo/CorrectorCombo';
import { DepartmentCombo } from '@/components/DepartmentCombo/DepartmentCombo';
import { EditionCombo } from '@/components/EditionCombo/EditionCombo';
import { EditorCombo } from '@/components/EditorCombo/EditorCombo';
import type { DepartmentValueItem } from '@/components/DepartmentCombo/DepartmentCombo';
import type { EditionValueItem } from '@/components/EditionCombo/EditionCombo';
import type { EditorValueItem } from '@/components/EditorCombo/EditorCombo';
import { DateAutofillInput } from '@/components/ui/DateAutofillInput/DateAutofillInput';
import { PaperBookPlanBacklogCombo } from '@/components/ui/PaperBookPlanBacklogCombo/PaperBookPlanBacklogCombo';
import { useEditionEditorDepartmentHandlers } from '@/hooks/useEditionEditorDepartmentHandlers';
import {
  getActiveProjectsSnapshot,
  invalidateReadyMonthProjectLists,
  patchActiveTableListCaches,
  setActiveProjectsSnapshot,
  syncReadyMonthCachesAfterProjectUpdated,
} from '@/lib/projectListQueryCache';
import { isProjectExcludedFromActiveTable } from '@/utils/projectStatuses';
import { ProjectFilesPopover } from '@/components/ProjectFilesPopover/ProjectFilesPopover';
import type { ProjectFileDraftItem } from '@/components/ProjectFilesPopover/ProjectFilesPopover.types';
import {
  formatAuthorSheetsOneDecimal,
  isSupportedProjectMetricsFile,
  tryComputeAuthorSheetsAndPagesFromFile,
} from '@/lib/projectFileMetrics/authorSheetsCalculator';
import { omitPriceKeys } from '@/lib/omitPriceKeys';
import { getSocket } from '@/hooks/useRealtimeSync';
import { ProjectNotesPopover, type ProjectNoteDraftItem } from '@/components/ProjectNotesPopover/ProjectNotesPopover';
import { getEmailAttachFilesButtonLabel, getAddProjectDraftNotesButtonLabel } from '@/utils/emailAttachFilesLabel';
import './AddProjectModal.css';

interface AddProjectModalProps {
  open: boolean;
  onClose: () => void;
}

const defaultState = 'Добавлен';

const PROJECT_TYPE_OPTIONS = PROJECT_TYPES.map((key) => ({
  value: key,
  label: PROJECT_TYPE_DISPLAY[key].text,
}));

type CreateProjectModalPayload = {
  trimmedName: string;
  projectType: ProjectTypeValue;
  authors: AuthorValueItem[];
  readers: ReaderEntryItem[];
  contractorEntries: ContractorEntryItem[];
  producers: ProducerValueItem[];
  techeditors: TecheditorValueItem[];
  correctors: CorrectorValueItem[];
  pagesNum: number;
  authorSheetsNum?: number;
  state: string;
  deadline?: string;
  asta?: string;
  ase?: string;
  rin?: string;
  paperBookPlan?: string;
  isBacklog?: boolean;
  theme?: string;
  rights?: string;
  editionId?: string | null;
  editorId?: string | null;
  changed_by_user_id?: string | null;
  pendingFiles: ProjectFileDraftItem[];
  pendingNotes: ProjectNoteDraftItem[];
  userId: string | null;
  /** Тост при ошибке клиентского расчёта АЛ/страниц при загрузке файла после создания проекта */
  onProjectFileMetricsError?: (message: string) => void;
  hidePrices?: boolean;
};

type AddProjectFilePipelineProgress =
  | { phase: 'metrics'; fileName: string; index: number; total: number }
  | { phase: 'server'; fileName: string; index: number; total: number; pct: number }
  | { phase: 'storage'; fileName: string; index: number; total: number; pct: number };

const ADD_PROJECT_PIPELINE_TITLES: Record<AddProjectFilePipelineProgress['phase'], string> = {
  metrics: 'Подсчёт авторских листов',
  server: 'Загрузка на сервер',
  storage: 'Загрузка в хранилище',
};

function formatAddProjectPipelineLabel(progress: AddProjectFilePipelineProgress | null): string {
  if (!progress) return 'Загрузка файлов';
  const base = ADD_PROJECT_PIPELINE_TITLES[progress.phase];
  const countSuffix = progress.total > 1 ? ` ${progress.index}/${progress.total}` : '';
  if (progress.phase === 'metrics') return `${base}${countSuffix}`;
  return `${base}${countSuffix} ${progress.pct}%`;
}

type ProjectFormMetricsReplaceDialog =
  | {
      kind: 'both' | 'pages' | 'sheets';
      targets: { pages: string; sheets: string };
    };

async function createProjectTransaction(
  vars: CreateProjectModalPayload,
  onFilePipelineProgress?: Dispatch<SetStateAction<AddProjectFilePipelineProgress | null>>
): Promise<Project> {
  const createBody = {
    name: vars.trimmedName,
    project_type: vars.projectType,
    reader_entries: entriesToReaderInputs(vars.readers),
    author_ids: vars.authors.map((a) => a.id),
    project_contracts: entriesToInputs(vars.contractorEntries),
    producer_ids: vars.producers.map((p) => p.id),
    techeditor_ids: vars.techeditors.map((t) => t.id),
    corrector_ids: vars.correctors.map((c) => c.id),
    pages: vars.pagesNum,
    author_sheets: vars.authorSheetsNum ?? null,
    state: vars.state,
    deadline: vars.deadline,
    asta: vars.asta || null,
    ase: vars.ase || null,
    rin: vars.rin || null,
    paper_book_plan: vars.paperBookPlan || null,
    is_backlog: vars.isBacklog ?? false,
    theme: vars.theme?.trim() || null,
    rights: vars.rights?.trim() || null,
    edition_id: vars.editionId?.trim() || null,
    editor_id: vars.editorId?.trim() || null,
    changed_by_user_id: vars.changed_by_user_id ?? undefined,
  };
  const project = await api.projects.create(vars.hidePrices ? omitPriceKeys(createBody) : createBody);

  try {
    const pending = vars.pendingFiles;
    for (let i = 0; i < pending.length; i++) {
      const entry = pending[i]!;
      const nameSuffix = pending.length > 1 ? ` (${i + 1} из ${pending.length})` : '';
      const displayName = `${entry.file.name}${nameSuffix}`;

      let clientMetrics: { authorSheets: number; pages: number } | null = null;

      if (entry.file_metrics_unavailable) {
        clientMetrics = null;
      } else if (
        entry.file_author_sheets != null &&
        entry.file_pages != null &&
        Number.isFinite(Number(entry.file_author_sheets)) &&
        Number.isFinite(Number(entry.file_pages))
      ) {
        clientMetrics = {
          authorSheets: Number(entry.file_author_sheets),
          pages: Number(entry.file_pages),
        };
      } else if (isSupportedProjectMetricsFile(entry.file)) {
        onFilePipelineProgress?.({
          phase: 'metrics',
          fileName: displayName,
          index: i + 1,
          total: pending.length,
        });
        const metricsTry = await tryComputeAuthorSheetsAndPagesFromFile(entry.file);
        if (!metricsTry.ok) {
          vars.onProjectFileMetricsError?.(
            `Не удалось вычислить АЛ/страницы для «${entry.file.name}»: ${metricsTry.errorMessage}`
          );
          clientMetrics = null;
        } else {
          clientMetrics = metricsTry.metrics;
        }
      }

      onFilePipelineProgress?.({
        phase: 'server',
        fileName: displayName,
        index: i + 1,
        total: pending.length,
        pct: 0,
      });

      await api.projectFiles.upload(
        project.id,
        entry.file,
        vars.userId,
        (xhrPct) => {
          onFilePipelineProgress?.({
            phase: 'server',
            fileName: displayName,
            index: i + 1,
            total: pending.length,
            pct: Math.min(100, Math.max(0, Math.round(xhrPct))),
          });
        },
        entry.file_type ?? null,
        clientMetrics
      );

      onFilePipelineProgress?.((prev) => {
        if (!prev || prev.fileName !== displayName) return prev;
        if (prev.phase === 'server') {
          return {
            phase: 'storage',
            fileName: displayName,
            index: i + 1,
            total: pending.length,
            pct: 100,
          };
        }
        if (prev.phase === 'storage') {
          return { ...prev, pct: Math.max(prev.pct, 100) };
        }
        return prev;
      });
    }
    onFilePipelineProgress?.(null);
    for (const item of vars.pendingNotes) {
      const text = item.content.trim();
      if (text) {
        await api.projectNotes.create(project.id, {
          note_content: text,
          created_by: vars.userId,
        });
      }
    }
  } catch (err) {
    onFilePipelineProgress?.(null);
    try {
      await api.projects.delete(project.id);
    } catch {
      /* откат удаления уже залогирован на сервере */
    }
    throw err;
  }

  return project;
}

export function AddProjectModal({ open, onClose }: AddProjectModalProps) {
  const queryClient = useQueryClient();
  const { user, capabilities } = useAuth();
  const hidePrices = !capabilities.prices;
  const { showError: showToastError } = useToast();
  const filesAnchorRef = useRef<HTMLDivElement>(null);
  const notesAnchorRef = useRef<HTMLDivElement>(null);

  const [name, setName] = useState('');
  const [projectType, setProjectType] = useState<ProjectTypeValue>('Common');
  const [readers, setReaders] = useState<ReaderEntryItem[]>([]);
  const [authors, setAuthors] = useState<AuthorValueItem[]>([]);
  const [contractorEntries, setContractorEntries] = useState<ContractorEntryItem[]>([]);
  const [producers, setProducers] = useState<ProducerValueItem[]>([]);
  const [techeditors, setTecheditors] = useState<TecheditorValueItem[]>([]);
  const [correctors, setCorrectors] = useState<CorrectorValueItem[]>([]);
  const [departmentValue, setDepartmentValue] = useState<DepartmentValueItem | null>(null);
  const [editionValue, setEditionValue] = useState<EditionValueItem | null>(null);
  const [editorValue, setEditorValue] = useState<EditorValueItem | null>(null);
  const [theme, setTheme] = useState('');
  const [rights, setRights] = useState('');
  const [pages, setPages] = useState('');
  const [authorSheets, setAuthorSheets] = useState('');
  const pagesFormRef = useRef('');
  const authorSheetsFormRef = useRef('');
  pagesFormRef.current = pages;
  authorSheetsFormRef.current = authorSheets;
  const [state, setState] = useState(defaultState);
  const [deadline, setDeadline] = useState('');
  const [asta, setAsta] = useState('');
  const [ase, setAse] = useState('');
  const [rin, setRin] = useState('');
  const [paperBookPlan, setPaperBookPlan] = useState('');
  const [isBacklog, setIsBacklog] = useState(false);
  const [error, setError] = useState('');
  const [pendingFiles, setPendingFiles] = useState<ProjectFileDraftItem[]>([]);
  const [pendingNotes, setPendingNotes] = useState<ProjectNoteDraftItem[]>([]);
  const [filesPopoverOpen, setFilesPopoverOpen] = useState(false);
  const [notesPopoverOpen, setNotesPopoverOpen] = useState(false);
  const [filePipelineProgress, setFilePipelineProgress] = useState<AddProjectFilePipelineProgress | null>(null);
  const createPersonPresetRef = useRef<CreatePersonRolePreset | null>(null);
  const openCatalogManage = useCatalogManageOpener();
  const canManageCatalog = useCanManageCatalog();
  const [modalSearchFlushEpoch, setModalSearchFlushEpoch] = useState(0);
  const [metricsReplaceDialog, setMetricsReplaceDialog] = useState<ProjectFormMetricsReplaceDialog | null>(null);
  const metricsReplaceDialogRef = useRef<ProjectFormMetricsReplaceDialog | null>(null);
  metricsReplaceDialogRef.current = metricsReplaceDialog;

  const { onDepartmentChange, onEditionChange, onEditorChange } = useEditionEditorDepartmentHandlers(
    setDepartmentValue,
    setEditionValue,
    setEditorValue
  );

  useEffect(() => {
    if (open) setModalSearchFlushEpoch((n) => n + 1);
  }, [open]);

  const draftTitleName = name.trim() || 'Новый проект';
  const nonEmptyNotesCount = useMemo(
    () => pendingNotes.filter((n) => n.content.trim().length > 0).length,
    [pendingNotes]
  );

  const resetFormState = useCallback(() => {
    setName('');
    setProjectType('Common');
    setReaders([]);
    setAuthors([]);
    setContractorEntries([]);
    setProducers([]);
    setTecheditors([]);
    setCorrectors([]);
    setDepartmentValue(null);
    setEditionValue(null);
    setEditorValue(null);
    setTheme('');
    setRights('');
    setPages('');
    setAuthorSheets('');
    setState(defaultState);
    setDeadline('');
    setAsta('');
    setAse('');
    setRin('');
    setPaperBookPlan('');
    setIsBacklog(false);
    setError('');
    setPendingFiles([]);
    setPendingNotes([]);
    setFilePipelineProgress(null);
    setFilesPopoverOpen(false);
    setNotesPopoverOpen(false);
    setMetricsReplaceDialog(null);
  }, []);

  const closeModalUi = useCallback(() => {
    setFilesPopoverOpen(false);
    setNotesPopoverOpen(false);
    setMetricsReplaceDialog(null);
    onClose();
  }, [onClose]);

  const resetAndClose = useCallback(() => {
    resetFormState();
    onClose();
  }, [onClose, resetFormState]);

  useEffect(() => {
    const socket = getSocket();
    const handler = (data: { pct: number; phase: string }) => {
      if (data.phase !== 'storage') return;
      setFilePipelineProgress((prev) => {
        if (!prev || prev.phase === 'metrics') return prev;
        const nextPct = Math.min(100, Math.max(0, Math.round(Number(data.pct))));
        if (prev.phase === 'server') {
          return {
            phase: 'storage',
            fileName: prev.fileName,
            index: prev.index,
            total: prev.total,
            pct: nextPct,
          };
        }
        if (prev.phase === 'storage') {
          return { ...prev, pct: Math.max(prev.pct, nextPct) };
        }
        return prev;
      });
    };
    socket.on('file:upload:progress', handler);
    return () => {
      socket.off('file:upload:progress', handler);
    };
  }, []);

  const createMutation = useMutation({
    mutationFn: (vars: CreateProjectModalPayload) =>
      createProjectTransaction(
        {
          ...vars,
          onProjectFileMetricsError: (msg) => showToastError(msg),
        },
        setFilePipelineProgress
      ),
    onSuccess: (data) => {
      const prepend = (prev: Project[] | undefined) => {
        if (!prev) return [data];
        const exists = prev.some((p) => p.id === data.id);
        if (exists) return prev;
        return [data, ...prev];
      };
      if (isProjectExcludedFromActiveTable(data.state)) {
        const activeProjects = getActiveProjectsSnapshot(queryClient);
        if (activeProjects) {
          setActiveProjectsSnapshot(
            queryClient,
            activeProjects.filter((project) => project.id !== data.id)
          );
        }
        const sync = syncReadyMonthCachesAfterProjectUpdated(queryClient, data);
        if (sync.invalidateSummaries) {
          void queryClient.invalidateQueries({ queryKey: ['ready-month-summaries'] });
        }
        invalidateReadyMonthProjectLists(queryClient, sync.readyMonthLists);
      } else {
        patchActiveTableListCaches(queryClient, prepend);
      }
      queryClient.invalidateQueries({ queryKey: ['authors'] });
      queryClient.invalidateQueries({ queryKey: ['readers'] });
      resetAndClose();
    },
    onError: (err: Error) => {
      setError(err.message);
      showToastError(err.message);
    },
    onSettled: () => {
      setFilePipelineProgress(null);
    },
  });

  const onDraftFileMarkedText = useCallback((draftItem: ProjectFileDraftItem) => {
    if (draftItem.file_metrics_unavailable) return;
    const as = draftItem.file_author_sheets;
    const pg = draftItem.file_pages;
    if (as == null || pg == null) return;
    const authorN = Number(as);
    const pagesN = Number(pg);
    if (!Number.isFinite(authorN) || !Number.isFinite(pagesN) || authorN < 0 || pagesN < 0) return;

    const pagesStrTarget = String(Math.max(0, Math.round(pagesN)));
    const sheetsStrTarget = formatAuthorSheetsOneDecimal(authorN);

    const pagesTrim = pagesFormRef.current.trim();
    const sheetsTrim = authorSheetsFormRef.current.trim();
    const pagesEmpty = !pagesTrim;
    const sheetsEmpty = !sheetsTrim;

    if (pagesEmpty && sheetsEmpty) {
      setPages(pagesStrTarget);
      setAuthorSheets(sheetsStrTarget);
      return;
    }
    if (!pagesEmpty && !sheetsEmpty) {
      setMetricsReplaceDialog({
        kind: 'both',
        targets: { pages: pagesStrTarget, sheets: sheetsStrTarget },
      });
      return;
    }
    if (!pagesEmpty && sheetsEmpty) {
      setAuthorSheets(sheetsStrTarget);
      setMetricsReplaceDialog({
        kind: 'pages',
        targets: { pages: pagesStrTarget, sheets: sheetsStrTarget },
      });
      return;
    }
    setPages(pagesStrTarget);
    setMetricsReplaceDialog({
      kind: 'sheets',
      targets: { pages: pagesStrTarget, sheets: sheetsStrTarget },
    });
  }, []);

  const openFilesPopover = useCallback(() => {
    setNotesPopoverOpen(false);
    setFilesPopoverOpen((v) => !v);
  }, []);

  const openNotesPopover = useCallback(() => {
    setFilesPopoverOpen(false);
    setNotesPopoverOpen((v) => !v);
  }, []);

  const openCreatePerson = useCallback(
    (preset: CreatePersonRolePreset | null) => {
      createPersonPresetRef.current = preset;
      openCatalogManage({
        tab: 'persons',
        rolePreset: preset,
        editionIdPreset: preset === 'editor' ? editionValue?.id ?? null : null,
        onPersonCreated: (payload) => {
          handleCreatePersonCreatedRef.current(payload);
        },
      });
    },
    [openCatalogManage, editionValue?.id]
  );

  /** Автоподхват только юрлица чтеца (не студии — студия ≠ контрагент проекта). */
  const appendLegalEntityFromReader = useCallback((reader: PersonSearchResult) => {
    const le = reader.legal_entity;
    if (!le?.id) return;
    setContractorEntries((prev) => {
      if (prev.some((e) => e.contractor_id === le.id)) return prev;
      return [
        ...prev,
        {
          key: Math.random().toString(36).slice(2),
          contractor_id: le.id,
          contractor_name: le.name,
          service_id: null as string | null,
          service_name: null as string | null,
          service_price: null as number | null,
          price_input: '',
          is_price_fixed: false,
          is_contractor_included_in_budget: false,
        },
      ];
    });
  }, []);

  const handleCreatePersonCreated = useCallback(
    (payload: CreatePersonCreatedPayload) => {
      if (payload.authors.length) {
        setAuthors((prev) => {
          const ids = new Set(prev.map((a) => a.id));
          const next = [...prev];
          for (const a of payload.authors) {
            const item = { id: a.id, name: a.name };
            if (!ids.has(item.id)) {
              next.push(item);
              ids.add(item.id);
            }
          }
          return next;
        });
      }
      if (payload.readers.length) {
        setReaders((prev) => {
          const ids = new Set(prev.map((r) => r.id).filter(Boolean));
          const next = [...prev];
          for (const r of payload.readers) {
            const entry = readerCatalogToEntry(r);
            if (!ids.has(entry.id)) {
              next.push(entry);
              ids.add(entry.id);
            }
          }
          return next;
        });
        for (const r of payload.readers) {
          appendLegalEntityFromReader(r);
        }
      } else if (createPersonPresetRef.current === null) {
        setReaders((prev) => {
          const ids = new Set(prev.map((r) => r.id).filter(Boolean));
          if (ids.has(payload.person.id)) return prev;
          return [...prev, readerCatalogToEntry(payload.person)];
        });
      }
      if (payload.editor) {
        const ed = payload.editor;
        setEditorValue({
          id: ed.id,
          name: ed.name,
          edition_id: ed.edition_id,
          edition_name: ed.edition_name,
          department_id: ed.department_id,
          department_code: ed.department_code,
        });
      }
    },
    [appendLegalEntityFromReader]
  );

  const handleCreatePersonCreatedRef = useRef(handleCreatePersonCreated);
  handleCreatePersonCreatedRef.current = handleCreatePersonCreated;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const trimmedName = name.trim();
    if (!trimmedName) {
      setError('Укажите название проекта');
      return;
    }

    const pagesNum = pages ? parseInt(pages.replace(',', '.'), 10) : 0;
    const authorSheetsNum = authorSheets ? parseFloat(authorSheets.replace(',', '.')) : undefined;

    const deadlineForApi = deadline.trim()
      ? (() => {
          try {
            const d = parse(deadline.trim(), 'dd.MM.yyyy', new Date());
            return isValid(d) ? format(d, 'yyyy-MM-dd') : undefined;
          } catch {
            return undefined;
          }
        })()
      : undefined;

    const paperBookPlanForApi = isBacklog
      ? undefined
      : paperBookPlan.trim()
        ? (() => {
            try {
              const d = parse(paperBookPlan.trim(), 'dd.MM.yyyy', new Date());
              return isValid(d) ? format(d, 'yyyy-MM-dd') : undefined;
            } catch {
              return undefined;
            }
          })()
        : undefined;

    if (pages && (isNaN(pagesNum) || pagesNum < 0)) {
      setError('Страницы должны быть числом ≥ 0');
      return;
    }
    if (authorSheets && (authorSheetsNum === undefined || isNaN(authorSheetsNum) || authorSheetsNum < 0)) {
      setError('Авторские листы должны быть числом ≥ 0');
      return;
    }

    for (const r of readers) {
      if (r.id && !isValidReaderTimeInput(r.time_input)) {
        setError('Введите время по чтецу в формате чч:мм:сс или оставьте пустым');
        return;
      }
    }

    setFilesPopoverOpen(false);
    setNotesPopoverOpen(false);
    setMetricsReplaceDialog(null);

    createMutation.mutate({
      trimmedName,
      projectType,
      authors,
      readers,
      contractorEntries,
      producers,
      techeditors,
      correctors,
      pagesNum,
      authorSheetsNum,
      state: state.trim() || defaultState,
      deadline: deadlineForApi,
      asta: asta.trim() || undefined,
      ase: ase.trim() || undefined,
      rin: rin.trim() || undefined,
      paperBookPlan: paperBookPlanForApi,
      isBacklog,
      theme: theme.trim() || undefined,
      rights: rights.trim() || undefined,
      editionId: editionValue?.id ?? undefined,
      editorId: editorValue?.id ?? undefined,
      changed_by_user_id: user?.id ?? undefined,
      pendingFiles,
      pendingNotes,
      userId: user?.id ?? null,
      hidePrices,
    });
  };

  /** Не создаём проект по Enter в полях — только по кнопке «Создать». */
  const handleFormKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.key !== 'Enter') return;
    if ((e.target as HTMLElement).tagName === 'TEXTAREA') return;
    e.preventDefault();
  };

  return (
    <>
    <Modal
      open={open}
      onClose={createMutation.isPending ? closeModalUi : resetAndClose}
      title="Добавить проект"
      headerVariant="island"
      size="md"
      className="add-project-modal"
    >
      <form onSubmit={handleSubmit} onKeyDown={handleFormKeyDown} className="form">
        <div
          className="form__row form__row_col"
          style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}
        >
          <StatusSelect
            value={state}
            onChange={setState}
            fullWidth
          />
          <SelectDropdown
            options={PROJECT_TYPE_OPTIONS}
            value={projectType}
            onChange={(v) => setProjectType(v as ProjectTypeValue)}
            fullWidth
            renderValue={(selected) =>
              selected ? (
                <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', justifyContent: 'flex-start', minWidth: 0, flex: 1, textAlign: 'left' }}>
                  <span
                    style={{
                      color: getProjectTypeColor(selected.value),
                      fontWeight: 'bold',
                      fontSize: selected.value === 'Common' ? '2rem' : '1.25rem',
                      lineHeight: 1,
                    }}
                  >
                    <ProjectTypeSymbol symbol={PROJECT_TYPE_DISPLAY[selected.value as ProjectTypeValue].label} />
                  </span>
                  <span>{PROJECT_TYPE_DISPLAY[selected.value as ProjectTypeValue].text}</span>
                </span>
              ) : null
            }
            renderOption={(opt) => (
              <>
                <span
                  style={{
                    color: getProjectTypeColor(opt.value),
                    fontWeight: 'bold',
                    minWidth: '2.25rem',
                    textAlign: 'center',
                    fontSize: opt.value === 'Common' ? '2rem' : '1.25rem',
                    lineHeight: 1,
                  }}
                >
                  <ProjectTypeSymbol symbol={PROJECT_TYPE_DISPLAY[opt.value as ProjectTypeValue].label} />
                </span>
                <span>{PROJECT_TYPE_DISPLAY[opt.value as ProjectTypeValue].text}</span>
              </>
            )}
          />
          <ProducerCombo
            value={producers}
            onChange={setProducers}
            placeholder="Продюсер"
            debounceFlushKey={modalSearchFlushEpoch}
          />
          <TecheditorCombo
            value={techeditors}
            onChange={setTecheditors}
            placeholder="Техредактор"
            debounceFlushKey={modalSearchFlushEpoch}
          />
          <CorrectorCombo
            value={correctors}
            onChange={setCorrectors}
            placeholder="Корректор"
            debounceFlushKey={modalSearchFlushEpoch}
          />
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Название проекта *"
            required
            fullWidth
            autoFocus
          />
          <div className="add-project-modal__field-with-action">
            <div className="add-project-modal__field-with-action-field">
              <AuthorCombo
                value={authors}
                onChange={setAuthors}
                placeholder="Автор"
                debounceFlushKey={modalSearchFlushEpoch}
              />
            </div>
            {canManageCatalog ? (
              <button
                type="button"
                className="add-project-modal__inline-add"
                aria-label="Добавить автора"
                title="Новая персона — автор"
                disabled={createMutation.isPending}
                onClick={() => openCreatePerson('author')}
              >
                <AddIcon size={20} />
              </button>
            ) : null}
          </div>
          <ReaderPriceList
            value={readers}
            onChange={setReaders}
            hidePrices={hidePrices}
            debounceFlushKey={modalSearchFlushEpoch}
            onReaderSelect={(reader) => {
              appendLegalEntityFromReader(reader);
            }}
            trailingSlot={
              canManageCatalog ? (
                <button
                  type="button"
                  className="add-project-modal__inline-add"
                  aria-label="Добавить персону"
                  title="Новая персона"
                  disabled={createMutation.isPending}
                  onClick={() => openCreatePerson(null)}
                >
                  <AddIcon size={20} />
                </button>
              ) : undefined
            }
          />
          <ContractorServiceList
            value={contractorEntries}
            onChange={setContractorEntries}
            hidePrices={hidePrices}
            debounceFlushKey={modalSearchFlushEpoch}
          />
          <Input
            value={pages}
            onChange={(e) => setPages(e.target.value.replace(/[^\d]/g, ''))}
            placeholder="Страницы"
            fullWidth
          />
          <Input
            value={authorSheets}
            onChange={(e) => {
              const v = e.target.value.replace(',', '.').replace(/[^\d.]/g, '');
              const parts = v.split('.');
              if (parts.length <= 1) setAuthorSheets(v);
              else setAuthorSheets(parts[0] + '.' + parts.slice(1).join(''));
            }}
            placeholder="Авторские листы"
            fullWidth
          />
          <DateAutofillInput
            value={deadline}
            onChange={setDeadline}
            placeholder="Дедлайн"
            fullWidth
          />
          <PaperBookPlanBacklogCombo
            value={paperBookPlan}
            onChange={setPaperBookPlan}
            isBacklog={isBacklog}
            onBacklogChange={setIsBacklog}
            fullWidth
          />
          <Input value={rights} onChange={(e) => setRights(e.target.value)} placeholder="Права" fullWidth />
          <DepartmentCombo
            value={departmentValue}
            onChange={onDepartmentChange}
            placeholder="Подразделение"
            debounceFlushKey={modalSearchFlushEpoch}
          />
          <EditionCombo
            value={editionValue}
            onChange={onEditionChange}
            departmentId={departmentValue?.id ?? null}
            placeholder="Редакция"
            onCreateError={(e) => setError(e.message)}
            debounceFlushKey={modalSearchFlushEpoch}
          />
          <div className="add-project-modal__field-with-action">
            <div className="add-project-modal__field-with-action-field">
              <EditorCombo
                value={editorValue}
                onChange={onEditorChange}
                departmentId={departmentValue?.id ?? null}
                placeholder="Редактор"
                debounceFlushKey={modalSearchFlushEpoch}
              />
            </div>
            {canManageCatalog ? (
              <button
                type="button"
                className="add-project-modal__inline-add"
                aria-label="Добавить редактора"
                title="Новая персона — редактор"
                disabled={createMutation.isPending}
                onClick={() => openCreatePerson('editor')}
              >
                <AddIcon size={20} />
              </button>
            ) : null}
          </div>
          <Input value={theme} onChange={(e) => setTheme(e.target.value)} placeholder="Тематика" fullWidth />
          <Input value={asta} onChange={(e) => setAsta(e.target.value)} placeholder="ASTA" fullWidth />
          <Input
            value={ase}
            onChange={(e) => setAse(e.target.value)}
            placeholder="ASE"
            maxLength={32}
            fullWidth
          />
          <Input value={rin} onChange={(e) => setRin(e.target.value)} placeholder="РИН" fullWidth />
          <div className="add-project-modal__attach-row">
            <div ref={filesAnchorRef} className="add-project-modal__attach-anchor">
              <Button
                type="button"
                variant="outlined"
                fullWidth
                onClick={openFilesPopover}
                disabled={createMutation.isPending}
              >
                {getEmailAttachFilesButtonLabel(pendingFiles.length)}
              </Button>
            </div>
            <div ref={notesAnchorRef} className="add-project-modal__attach-anchor">
              <Button
                type="button"
                variant="outlined"
                fullWidth
                onClick={openNotesPopover}
                disabled={createMutation.isPending}
              >
                {getAddProjectDraftNotesButtonLabel(nonEmptyNotesCount)}
              </Button>
            </div>
          </div>
          <ProjectFilesPopover
            open={filesPopoverOpen}
            onClose={() => setFilesPopoverOpen(false)}
            anchorEl={filesAnchorRef.current}
            localDraft={{
              files: pendingFiles,
              onFilesChange: setPendingFiles,
              titleName: draftTitleName,
              onDraftFileMarkedText,
            }}
          />
          <ProjectNotesPopover
            open={notesPopoverOpen}
            onClose={() => setNotesPopoverOpen(false)}
            anchorEl={notesAnchorRef.current}
            localDraft={{
              items: pendingNotes,
              onItemsChange: setPendingNotes,
              titleName: draftTitleName,
            }}
          />
          {error && (
            <span className="form__error" role="alert">
              {error}
            </span>
          )}
          <div className="form__footer form__footer_modal">
            <Button
              variant="outlined"
              type="button"
              onClick={createMutation.isPending ? closeModalUi : resetAndClose}
            >
              Отмена
            </Button>
            <Button
              type="submit"
              variant="primary"
              className={
                createMutation.isPending ? 'add-project-modal__submit-btn add-project-modal__submit-btn_loading' : 'add-project-modal__submit-btn'
              }
              disabled={createMutation.isPending}
              aria-busy={createMutation.isPending || undefined}
            >
              {createMutation.isPending ? (
                <span className="add-project-modal__submit-loading" role="status" aria-live="polite">
                  <span className="add-project-modal__submit-spinner" aria-hidden="true" />
                  <span>{formatAddProjectPipelineLabel(filePipelineProgress)}</span>
                </span>
              ) : (
                'Создать'
              )}
            </Button>
          </div>
        </div>
      </form>
    </Modal>
    <ChoiceDialog
      open={metricsReplaceDialog !== null}
      onClose={() => setMetricsReplaceDialog(null)}
      title="Данные из файла"
      description={
        metricsReplaceDialog?.kind === 'both'
          ? 'Заменить данные в полях «Авторские листы» и «Страницы» на данные из файла?'
          : metricsReplaceDialog?.kind === 'pages'
            ? 'Заменить данные в поле «Страницы» на данные из файла?'
            : metricsReplaceDialog?.kind === 'sheets'
              ? 'Заменить данные в поле «Авторские листы» на данные из файла?'
              : ''
      }
      actions={[
        {
          label: 'Отмена',
          variant: 'outlined',
          onClick: () => setMetricsReplaceDialog(null),
        },
        {
          label: 'Заменить',
          variant: 'primary',
          onClick: () => {
            const d = metricsReplaceDialogRef.current;
            if (!d) return;
            if (d.kind === 'both') {
              setPages(d.targets.pages);
              setAuthorSheets(d.targets.sheets);
            } else if (d.kind === 'pages') {
              setPages(d.targets.pages);
            } else {
              setAuthorSheets(d.targets.sheets);
            }
            setMetricsReplaceDialog(null);
          },
        },
      ]}
    />
    </>
  );
}
